import React, {useCallback, useEffect} from 'react';
import {Grid} from '@material-ui/core';
import {observer} from 'mobx-react-lite';
import {Parameter} from '@/Core/mobx/ParameterStore/Parameter';
import {Approval} from '@/Core/mobx/ParameterStore/Approval';
import {CategoryCard} from '../PreApprovals/CategoryCard';
import {PhoneFormatCustom, TextBox} from '@/Core/UI';
import {SelectInputElement} from '../PreQualification/Components/SelectInputElement';
import {fieldOptions} from '@/Core/mobx/ParameterStore/PrequalXref';
import {emailValidator, phoneValidator} from '@/Core/helpers/validators';
import DateOfBirthPicker from './DateOfBirthPicker';

type BasicInfoProps = {
    valueChanged: (p?: Parameter & {name?: string}, value?: any, name?: string) => void;
    parameters: Approval;
    setCategory: (entry: string) => void;
    index: number;
    setCompleted?: (completed: boolean) => void;
    reportMissingField?: (field: string | null) => void;
};

const BasicInfo = observer(
    ({valueChanged, parameters, setCategory, index, setCompleted, reportMissingField}: BasicInfoProps) => {
        const required = (value?: string) => (
            <span>
                {value && value} <span style={{color: 'red'}}>*</span>
            </span>
        );

        const phoneValue = useCallback(() => {
            const rawValue = parameters.phoneNumber?.value;
            if (!rawValue) return rawValue;

            const numericValue = rawValue.replace(/[^0-9]/g, '');
            return numericValue.slice(-10);
        }, [parameters.phoneNumber?.value]);

        useEffect(() => {
            setCompleted?.(
                parameters.firstName?.value !== '' &&
                    parameters.lastName?.value !== '' &&
                    phoneValidator(phoneValue() || '') &&
                    emailValidator(parameters.emailAddress?.value || '') &&
                    !!parameters.byear?.value &&
                    !!parameters.bmonth?.value &&
                    !!parameters.bday?.value &&
                    parameters.gender?.value !== '' &&
                    parameters.address?.value !== '' &&
                    parameters.addressCity?.value !== '' &&
                    parameters.state?.value !== '' &&
                    parameters.addressZipCode?.value !== '',
            );

            let missing: string | null = null;
            if (parameters.firstName?.value === '') missing = 'Client First Name';
            else if (parameters.lastName?.value === '') missing = 'Client Last Name';
            else if (!phoneValidator(phoneValue() || '')) missing = 'Phone Number';
            else if (!emailValidator(parameters.emailAddress?.value || '')) missing = 'Email Address';
            else if (!parameters.byear?.value || !parameters.bmonth?.value || !parameters.bday?.value)
                missing = 'Date Of Birth';
            else if (parameters.gender?.value === '') missing = 'Gender';
            else if (parameters.address?.value === '') missing = 'Street Address';
            else if (parameters.addressCity?.value === '') missing = 'City';
            else if (parameters.state?.value === '') missing = 'State';
            else if (parameters.addressZipCode?.value === '') missing = 'Zipcode';
            reportMissingField?.(missing);
        }, [
            setCompleted,
            reportMissingField,
            phoneValue,
            parameters.firstName?.value,
            parameters.lastName?.value,
            parameters.emailAddress?.value,
            parameters.byear?.value,
            parameters.bmonth?.value,
            parameters.bday?.value,
            parameters.gender?.value,
            parameters.address?.value,
            parameters.addressCity?.value,
            parameters.state?.value,
            parameters.addressZipCode?.value,
        ]);

        return (
            <CategoryCard title={'Basic Information'} setCategory={setCategory} index={index}>
                <Grid item xs={4}>
                    <TextBox
                        id={'firstName'}
                        label={required('Client First Name')}
                        value={parameters.firstName?.value}
                        onChange={(value) => valueChanged(parameters.firstName, value)}
                    />
                </Grid>
                <Grid item xs={4}>
                    <TextBox
                        id={'lastName'}
                        label={required('Client Last Name')}
                        value={parameters.lastName?.value}
                        onChange={(value) => valueChanged(parameters.lastName, value)}
                    />
                </Grid>
                <Grid item xs={2}>
                    <TextBox
                        id={'middleName'}
                        label={'Middle Initial'}
                        value={parameters.middleName?.value}
                        onChange={(value) => valueChanged(parameters.middleName, value)}
                    />
                </Grid>
                <Grid item xs={2}>
                    <TextBox
                        id={'suffix'}
                        label={'Suffix'}
                        value={parameters.suffix?.value}
                        onChange={(value) => valueChanged(parameters.suffix, value)}
                    />
                </Grid>
                <Grid item xs={4}>
                    <TextBox
                        error={!!parameters.phoneNumber.value && !phoneValidator(phoneValue())}
                        id={'phoneNumber'}
                        label={required('Phone Number')}
                        value={phoneValue()}
                        onChange={(value) => valueChanged(parameters.phoneNumber, value)}
                        placeholder="(323) 232-3123"
                        InputProps={{
                            inputComponent: PhoneFormatCustom,
                        }}
                    />
                </Grid>
                <Grid item xs={4}>
                    <TextBox
                        error={!!parameters.emailAddress.value && !emailValidator(parameters.emailAddress.value)}
                        id={'emailAddress'}
                        label={required('Email Address')}
                        value={parameters.emailAddress?.value}
                        onChange={(value) => valueChanged(parameters.emailAddress, value?.trim())}
                        placeholder="example@gmail.com"
                    />
                </Grid>
                <Grid item xs={4}>
                    <SelectInputElement
                        param={{
                            label: 'Gender',
                            options: fieldOptions.gender.options,
                            ...parameters.gender,
                            name: 'gender',
                        }}
                        required={required('')}
                        valueChanged={valueChanged}
                    />
                </Grid>
                <DateOfBirthPicker parameters={parameters} valueChanged={valueChanged} required={required('')} />
                <Grid item xs={4}>
                    <TextBox
                        id={'address'}
                        label={required('Street Address')}
                        value={parameters.address?.value}
                        onChange={(value) => valueChanged(parameters.address, value)}
                    />
                </Grid>
                <Grid item xs={4}>
                    <TextBox
                        id={'addressCity'}
                        label={required('City')}
                        value={parameters.addressCity?.value}
                        onChange={(value) => valueChanged(parameters.addressCity, value)}
                    />
                </Grid>
                <Grid item xs={4}>
                    <SelectInputElement
                        param={{
                            label: 'State',
                            options: fieldOptions.state.options,
                            ...parameters.state,
                            name: 'state',
                        }}
                        required={required('')}
                        valueChanged={valueChanged}
                    />
                </Grid>
                <Grid item xs={2}>
                    <TextBox
                        id={'addressZipCode'}
                        label={required('Zipcode')}
                        value={parameters.addressZipCode?.value}
                        onChange={(value) => valueChanged(parameters.addressZipCode, value)}
                    />
                </Grid>
            </CategoryCard>
        );
    },
);

export default BasicInfo;
